import cn from "classnames";
import { SyncService } from "frontend/services/syncService";
import { useBoardValue } from "frontend/state/board-state";
import { Box } from "frontend/ui-components/design-system/box/box";
import ActiveBoardUser from "frontend/user-presence/active-board-user";
import ProfileNew from "frontend/user-presence/profile-new";
import { useAtom, useAtomValue } from "jotai";
import { memo, useRef, useState } from "react";
import { RW } from "shared/datamodel/replicache-wrapper/mutators";
import { SidepanelType, sidePanelType, syncServiceAtom, userAtom } from "state-atoms";
import { useClientInfo, useCollaboratorIDs } from "subscriptions";
import { useOnClickOutside } from "usehooks-ts";
import styles from "./user-view.module.css";
import { SearchIcon } from "frontend/icons/search";
import { TextInput } from "frontend/ui-components/design-system/text-input";

const UserRow = ({
  id,
  syncService,
  search,
  setFollow,
}: {
  id: string;
  syncService: SyncService<RW> | null;
  search: string;
  setFollow: (id: string) => void;
}) => {
  const info = useClientInfo(syncService?.getReplicache(), id);
  const userInfo = info?.userInfo;

  if (search && !userInfo?.name.toLowerCase().includes(search.toLowerCase())) {
    return null;
  }

  return (
    <div className={styles.userRow} onClick={() => setFollow(id)}>
      <ActiveBoardUser clientID={id} syncService={syncService} variant="cim" />
      <div className={styles.userRowName}>
        <span>{userInfo?.name}</span>
      </div>
    </div>
  );
};

export const UserView = memo(({ reps, setFollow }: { reps: any; setFollow: (id: string) => void }) => {
  const [openedSidePanel, setOpenedSidePanel] = useAtom(sidePanelType);
  const isUserViewOpen = openedSidePanel === SidepanelType.userView;
  const floaterRef = useRef<HTMLDivElement>(null);
  const syncService = useAtomValue(syncServiceAtom);
  const currentUser = useAtomValue(userAtom);
  const collaboratorsIds = useCollaboratorIDs(reps, false, currentUser?.id?.toString());
  const [{ user }] = useBoardValue();
  const [search, setSearch] = useState("");

  useOnClickOutside(floaterRef, () => {
    setOpenedSidePanel(SidepanelType.none);
  });

  if (!isUserViewOpen) {
    return null;
  }

  return (
    <Box className={cn(styles.baseModal, styles.userViewContent)} ref={floaterRef}>
      <div className={styles.profile}>
        <ProfileNew isBoardHeader={true} syncService={syncService} shouldShowMenu={false} />
        <div className={styles.profileName}>{user?.name} (You)</div>
      </div>
      {collaboratorsIds.length > 0 && (
        <TextInput
          leftIcon={<SearchIcon scale={1.2} color="#7F869B" />}
          variant="small"
          placeholder="Find someone..."
          className={styles.searchInput}
          onChange={(e) => setSearch(e.target.value)}
        />
      )}
      {collaboratorsIds.length == 0 ? null : (
        <div className={styles.users}>
          {collaboratorsIds.map((id) => (
            <UserRow
              key={id}
              id={id}
              syncService={syncService}
              search={search}
              setFollow={(id) => {
                setFollow(id);
                setOpenedSidePanel(SidepanelType.none);
              }}
            />
          ))}
        </div>
      )}
    </Box>
  );
});

UserView.displayName = "UserView";
